import React, { useEffect, useState } from "react";
import { Card, Col, Row, Button, Spinner, ListGroup, Badge, ProgressBar, Nav, Tab } from "react-bootstrap";
import FalconCardHeader from "components/common/falconCardHeader";
import Background from "components/common/background";
import corner4 from "assets/img/illustrations/corner-4.png";
import axios from "axios";
import moment from "moment";
import { useTranslation } from "react-i18next";
import {
  faMapMarkerAlt,
  faClock,
  faTasks,
  faExclamationCircle,
  faCheckCircle,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import { toast } from "react-toastify";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import TaskDrawer from "../calender/taskDrawer";
import { useNavigate, Link } from "react-router-dom";
import { fetchRoleAndClaims, handleLoadingAndAccess, getStatusColorClassesForTile, renderTaskStatusBadge } from "../../assets/js/common";
import AdvanceTable from "components/common/advance-table/advanceTable";
import AdvanceTableProvider from "providers/advanceTableProvider";
import AdvanceTableSearchBox from "components/common/advance-table/advanceTableSearchBox";
import ManualPaginationFooter from "components/common/advance-table/manualPaginationFooter";
import SubtleBadge from "components/common/subtleBadge";
import useServerAdvanceTable from "hooks/useServerAdvanceTable";
import { getInitialPageSize } from "../../assets/js/common";
import { useAppContext } from "providers/appProvider";
import useSocket from '../../hooks/useSocket';

const EmployeeDashboard = () => {
  const [allTasks, setAllTasks] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [todayTasks, setTodayTasks] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation();
  const [hasAccess, setHasAccess] = useState(false);
  const userRole = localStorage.getItem("UserRole");
  const [isPostEndDrawerOpen, setIsPostEndDrawerOpen] = useState(false);
  const [endedTask, setEndedTask] = useState(null);
  const isEmployeeView = sessionStorage.getItem("isEmployeeView");
  const [overdueTasks, setOverdueTasks] = useState([]);
  const [showAllTodayTasks, setShowAllTodayTasks] = useState(false);
  const [showAllUpcomingTasks, setShowAllUpcomingTasks] = useState(false);
  const [showAllOverdueTasks, setShowAllOverdueTasks] = useState(false);
  const [completedTasks, setCompletedTasks] = useState([]);
  const [completedTotalCount, setCompletedTotalCount] = useState(0);
  const [completedTotalPages, setCompletedTotalPages] = useState(0);
  const [completedPageIndex, setCompletedPageIndex] = useState(0); // 0-based, matches Orders.js
  const [completedPageSize, setCompletedPageSize] = useState(getInitialPageSize("completedTasksTable"));
  const [completedSearchInput, setCompletedSearchInput] = useState("");
  const [debouncedCompletedSearch, setDebouncedCompletedSearch] = useState("");
  const [completedLoading, setCompletedLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("today"); // Default to today's tasks
  const { isAttendanceEnabled, setIsEmployeeView, config: { location }, attendancePhase, attendanceBusy, loadAttendance, clockInTime, workSeconds, handleClockIn, formatSecondsToHM} = useAppContext();
  const [taskStepCompletions, setTaskStepCompletions] = useState({});

  const checkAndUpdateOrderStatus = async (orderId) => {
    try {
      const summaryResponse = await axios.get(
        `${process.env.REACT_APP_API_URL}/orderRoute/${orderId}/jobStatusSummary`,
        {
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
      );
      const jobStatusSummary = summaryResponse.data;

      // Check if order is already Active
      if (jobStatusSummary && jobStatusSummary.orderStatus === "Active") {
        return; // Skip update if already Active
      }

      // Check if any job has status inProgress (with capital P as shown in logs)
      const hasInProgressJob =
        (jobStatusSummary && jobStatusSummary["1"] > 0) || // Status "1" = inprogress
        (jobStatusSummary && jobStatusSummary.inProgress > 0) || // Check for "inProgress" (with capital P)
        (jobStatusSummary && jobStatusSummary.inprogress > 0) || // Check for lowercase
        (jobStatusSummary && jobStatusSummary["in progress"] > 0); // Check for "in progress" string

      if (hasInProgressJob) {
        const updateResponse = await axios.patch(
          `${process.env.REACT_APP_API_URL}/orderRoute/updateOrderStatus/${orderId}`,
          { status: "Active" },
          {
            headers: {
              Authorization: process.env.REACT_APP_BasicAuthSecret,
            }
          }
        );
      }
    } catch (error) {
      console.error("Error checking/updating order status:", error);
      if (error.response) {
        console.error("Response data:", error.response.data);
        console.error("Response status:", error.response.status);
      }
    }
  };

  const fetchEmployeeTasks = async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const userId = localStorage.getItem("UserId");
      if (!userId) return;

      const startDate = moment().subtract(30, "days").format("YYYY-MM-DD");
      const endDate = moment().endOf("isoWeek").format("YYYY-MM-DD");

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/orderRoute/getTasksByCleanerScoped`,
        {
          params: { userId, startDate, endDate },
          headers: {
            Authorization: process.env.REACT_APP_BasicAuthSecret
          }
        }
      );
      const enrichedTasks = response.data || [];

      const today = moment().format("YYYY-MM-DD");

      const filteredTodayTasks = enrichedTasks.filter(task =>
        moment(task.WeekStartDate).format("YYYY-MM-DD") === today
      );

      const todayMoment = moment().startOf("day");

      const filteredOverdueTasks = enrichedTasks.filter(task => {
        const taskDate = moment(task.WeekStartDate).startOf("day");
        return (
          taskDate.isBefore(todayMoment) &&
          task.Status !== "2" &&
          task.Status !== 2
        );
      });

      const upcomingTasksForProgress = enrichedTasks.filter(task => {
        const taskDate = moment(task.WeekStartDate);
        return (
          taskDate.isAfter(moment(), "day") &&
          taskDate.isoWeek() === moment().isoWeek()
        );
      });

      const relevantTasks = [
        ...filteredTodayTasks,
        ...upcomingTasksForProgress
      ];

      const uniqueRelevantTasks = [
        ...new Map(
          relevantTasks.map(task => [
            task.OrderDetailId,
            task
          ])
        ).values()
      ];

      const orderDetailIds = uniqueRelevantTasks.map(
        task => task.OrderDetailId
      );

      const completionResponse = orderDetailIds.length
        ? await axios.post(
          `${process.env.REACT_APP_API_URL}/orderDetailSubtaskCompletionRoute/bulk`,
          { orderDetailIds },
          { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
        )
        : { data: [] };

      const newTaskStepCompletions = {};

      completionResponse.data.forEach(completion => {
        const orderDetailId = completion.OrderDetailId;

        if (!newTaskStepCompletions[orderDetailId]) {
          newTaskStepCompletions[orderDetailId] = {};
        }

        const key = (completion.ProposalSubtaskId || "").trim();

        newTaskStepCompletions[orderDetailId][key] = {
          CompletedAt: completion.CompletedAt,
          EmployeeId: completion.EmployeeId
        };
      });

      setTaskStepCompletions(newTaskStepCompletions);

      setAllTasks(enrichedTasks);
      setTodayTasks(filteredTodayTasks);
      setOverdueTasks(filteredOverdueTasks);
    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      if (!silent) setLoading(false); // stop spinner
    }
  };

  const fetchCompletedTasks = async (pageIndex, pageSize, search) => {
    setCompletedLoading(true);
    try {
      const userId = localStorage.getItem("UserId");
      if (!userId) return;

      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/orderRoute/getCompletedTasksByCleanerPaginated`,
        {
          params: { userId, page: pageIndex + 1, pageSize, search },
          headers: { Authorization: process.env.REACT_APP_BasicAuthSecret }
        }
      );

      setCompletedTasks(response.data.tasks || []);
      setCompletedTotalCount(response.data.totalCount || 0);
      setCompletedTotalPages(response.data.totalPages || 0);
    } catch (error) {
      console.error("Error fetching completed tasks:", error);
    } finally {
      setCompletedLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedCompletedSearch(completedSearchInput), 400);
    return () => clearTimeout(timer);
  }, [completedSearchInput]);

  useEffect(() => {
    setCompletedPageIndex(0);
  }, [debouncedCompletedSearch]);

  useEffect(() => {
    fetchCompletedTasks(completedPageIndex, completedPageSize, debouncedCompletedSearch);
  }, [completedPageIndex, completedPageSize, debouncedCompletedSearch]);

  const getTaskSortValue = (task) => {
    const day = moment(task.WeekStartDate).startOf("day");
    if (task.StartTime) {
      const local = moment.utc(task.StartTime).local();
      return day
        .clone()
        .hour(local.hour())
        .minute(local.minute())
        .second(local.second())
        .valueOf();
    }
    return day.clone().endOf("day").valueOf();
  };

  const compareTasksAsc = (a, b) => getTaskSortValue(a) - getTaskSortValue(b);

  const compareTasksDesc = (a, b) => getTaskSortValue(b) - getTaskSortValue(a);

  const handleStartJobClick = async (e, task) => {
    e.stopPropagation();

    if (isAttendanceEnabled && attendancePhase === "idle") {
       await handleClockIn();
     }

    const userId = localStorage.getItem("UserId");
    const now = moment();
    
    const updates = {
      Status: "1",
      ActualStartTime: now.toISOString(),        
      ActualStartDate: now.format("YYYY-MM-DD"), 
    };

    try {
      const response = await axios.patch(
        `${process.env.REACT_APP_API_URL}/orderRoute/updateOrderDetail`,
        { orderDetailId: task.OrderDetailId, updates },
        { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
      );

      if (response.status === 200) {
        const completionResults = await Promise.allSettled([
          axios.post(
            `${process.env.REACT_APP_API_URL}/orderDetailSubtaskCompletionRoute/complete`,
            { orderDetailId: task.OrderDetailId, proposalSubtaskId: "static-top-1", employeeId: userId },
            { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
          ),
          axios.post(
            `${process.env.REACT_APP_API_URL}/orderDetailSubtaskCompletionRoute/complete`,
            { orderDetailId: task.OrderDetailId, proposalSubtaskId: "static-top-2", employeeId: userId },
            { headers: { Authorization: process.env.REACT_APP_BasicAuthSecret } }
          ),
        ]);

        const nowIso = new Date().toISOString();

        setTaskStepCompletions(prev => ({
          ...prev,
          [task.OrderDetailId]: {
            ...(prev[task.OrderDetailId] || {}),
            "static-top-1": {
              CompletedAt: completionResults[0].status === "fulfilled"
                ? completionResults[0].value.data?.CompletedAt
                : nowIso,
              EmployeeId: userId,
            },
            "static-top-2": {
              CompletedAt: completionResults[1].status === "fulfilled"
                ? completionResults[1].value.data?.CompletedAt
                : nowIso,
              EmployeeId: userId,
            },
          }
        }));

        const updatedTask = {
          ...task,
          Status: updates.Status,
          ActualStartTime: updates.ActualStartTime,
          ActualStartDate: updates.ActualStartDate, 
        };

        setAllTasks(prev => prev.map(t => t.OrderDetailId === task.OrderDetailId ? updatedTask : t));
        setTodayTasks(prev => prev.map(t => t.OrderDetailId === task.OrderDetailId ? updatedTask : t));
        setOverdueTasks(prev => prev.map(t => t.OrderDetailId === task.OrderDetailId ? updatedTask : t));
        setSelectedTask(updatedTask);

        if (task.OrderId) {
          await checkAndUpdateOrderStatus(task.OrderId);
        }

        toast.success(t("jobStartedSuccessfully"), { position: "top-right", autoClose: 3000 });
      }
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error(t("taskNotExist"), { position: "top-right" });
        setTimeout(() => fetchEmployeeTasks(), 2000);
      } else {
        toast.error(t("jobStartError"));
      }
      console.error("Error starting job:", error);
    }
  };

  const formatTaskTimeRange = (weekStartDate, startTime, endTime) => {
    if (!startTime || !endTime) return "";

    const taskDate = moment(weekStartDate).format("YYYY-MM-DD");
    const start = moment.utc(`${taskDate}T${moment.utc(startTime).format("HH:mm:ss")}`).local();
    const end = moment.utc(`${taskDate}T${moment.utc(endTime).format("HH:mm:ss")}`).local();

    return `${start.format("HH:mm")} - ${end.format("HH:mm")}`;
  };

  const formatTaskDateTimeRange = (weekStartDate, startTime, endTime) => {
    if (!startTime || !endTime) return "";

    const taskDate = moment(weekStartDate).format("YYYY-MM-DD");
    const start = moment.utc(`${taskDate}T${moment.utc(startTime).format("HH:mm:ss")}`).local();
    const end = moment.utc(`${taskDate}T${moment.utc(endTime).format("HH:mm:ss")}`).local();

    return `${start.format("DD-MM-YYYY")} - ${start.format("HH:mm")} - ${end.format("HH:mm")}`;
  };

  const upcomingTasks = allTasks.filter(task => {
    const taskDate = moment(task.WeekStartDate);
    const today = moment();
    return taskDate.isAfter(today, 'day') && taskDate.isoWeek() === today.isoWeek();
  });

  const getTaskProgress = (task, stepCompletionsForTask = {}) => {
    const topSubtasks = ["static-top-1", "static-top-2"];
    const bottomSubtasks = ["static-bottom-1", "static-bottom-2"];
    const middleSubtaskIds = (task?.Subtasks || []).map(s => s.SubtaskId || s.ProposalSubtaskId);

    const allStepIds = [...topSubtasks, ...middleSubtaskIds, ...bottomSubtasks];
    const total = allStepIds.length;

    const completed = allStepIds.filter(id => !!stepCompletionsForTask[id]).length;
    const pending = total - completed;

    return { total, completed, pending };
  };

  // Completed Tasks Table Configuration
  const completedTasksColumns = [
    {
      accessorKey: "Relation.Name",
      header: t("clientName"),
      cell: ({ row: { original } }) => (
        <span>{original.Relation?.Name || "--"}</span>
      ),
    },
    {
      accessorKey: "location",
      header: t("branch"),
      cell: ({ row: { original } }) => (
        <span>
          {`${original.Relation?.StreetName || ''} ${original.Relation?.HouseNumber || ''}, ${original.Relation?.City || ''}`}
        </span>
      ),
    },
    {
      accessorKey: "date",
      header: t("date"),
      cell: ({ row: { original } }) => (
        <span>
          {moment(original.WeekStartDate).format("DD-MM-YYYY")}
        </span>
      ),
    },
    {
      accessorKey: "time",
      header: t("time"),
      cell: ({ row: { original } }) => (
        <span>
          {formatTaskTimeRange(original.WeekStartDate, original.StartTime, original.EndTime)}
        </span>
      ),
    },
    {
      accessorKey: "Task.TaskName",
      header: t("task"),
      cell: ({ row: { original } }) => (
        <span>{original.Task?.TaskName || "--"}</span>
      ),
    },
    {
      accessorKey: "Status",
      header: t("status"),
      cell: ({ row: { original } }) => renderStatusBadge(original.Status, t),
    },
  ];

  const completedTasksTable = useServerAdvanceTable({
    data: completedTasks,
    columns: completedTasksColumns,
    selection: false,
    sortable: true,
    pagination: true,
    manualPagination: true,
    pageCount: completedTotalPages,
    paginationState: { pageIndex: completedPageIndex, pageSize: completedPageSize },
    onPaginationChange: (updater) => {
      const next = typeof updater === "function"
        ? updater({ pageIndex: completedPageIndex, pageSize: completedPageSize })
        : updater;
      setCompletedPageIndex(next.pageIndex);
      setCompletedPageSize(next.pageSize);
    },
  });

  const OverdueTaskCard = ({ task, onClick, t, renderStatusBadge }) => {
    const { bg, text } = getStatusColorClassesForTile(task.Status);
    const taskDate = moment(task.WeekStartDate).format("DD-MM-YYYY");

    const isOverdue = moment().isAfter(moment(task.WeekStartDate));
    const cardBgClass = isOverdue ? "bg-danger bg-opacity-10" : bg;

    return (
      <Col xs={12} sm={6} lg={3}>
        <Card className={`cursor-pointer h-100 border px-3 py-2 card-hover ${cardBgClass}`} onClick={() => onClick(task)}>
          <Card.Body className={`p-2 data-font-colour d-flex flex-column justify-content-between`}>
            <div>
              <div className="d-flex justify-content-between data-font-colour align-items-center mb-2">
                <strong className="fs-9 text-truncate">
                  {task.Relation?.Name}
                </strong>
                {renderStatusBadge ? renderStatusBadge(task.Status, t) : task.Status}
              </div>
              <div className={`d-flex align-items-start mb-1 data-font-colour`}>
                <FontAwesomeIcon icon={faTasks} className="me-2 mt-1 icon-opacity" />
                <span className="data-font-colour data-font-size">{task.Task?.TaskName}</span>
              </div>
              <div className="mb-1 data-font-colour">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2 icon-opacity" />
                <a className="data-font-size"
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(task.Relation?.StreetName + ' ' + task.Relation?.HouseNumber + ', ' + task.Relation?.City)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {`${task.Relation?.StreetName || ''} ${task.Relation?.HouseNumber || ''}, ${task.Relation?.PostCode || ''} ${task.Relation?.City || ''}`}
                </a>
              </div>

              <div className={`d-flex align-items-start mb-2 data-font-colour`}>
                <FontAwesomeIcon icon={faClock} className="me-2 mt-1 icon-opacity data-font-colour" />
                <div className="data-font-size">
                  {formatTaskDateTimeRange(task.WeekStartDate, task.StartTime, task.EndTime)}
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-end align-items-end mt-2">
              <div className="align-self-end">
                {!task.ActualEndTime && (Number(task.Status) !== 1 ? (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="fw-semibold px-2 py-1 rounded-pill shadow-sm"
                    title={t("startCurrentJob")}
                    onClick={(e) => handleStartJobClick(e, task)}
                  >
                    {t("startJob")}
                  </Button>
                ) : (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="fw-semibold px-2 py-1 rounded-pill shadow-sm"
                    title={t("endCurrentJob")}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedTask(task);
                      setDrawerOpen(true);
                    }}
                  >
                    {t("endJob")}
                  </Button>
                )
                )}
              </div>
            </div>
          </Card.Body>
        </Card>
      </Col>
    );
  };

  const UpcomingTaskCard = ({ task, onClick, t, renderStatusBadge, stepCompletionsForTask = {} }) => {
    const { bg } = getStatusColorClassesForTile(task.Status);
    const weekday = moment(task.WeekStartDate).format("dddd").toLowerCase();
    const taskDay = t(`fullDaysName.${weekday}`);
    const { total, completed, pending } = getTaskProgress(task, stepCompletionsForTask);

    return (
      <Col xs={12} md={6} lg={4}>
        <Card className={`cursor-pointer h-100 border px-3 py-2 card-hover ${bg}`} onClick={() => onClick(task)}>
          <Card.Body className={`p-2 data-font-colour d-flex flex-column justify-content-between`}>
            <div>
              <div className="d-flex justify-content-between data-font-colour align-items-center mb-2">
                <strong className="fs-9 text-truncate">
                  {task.Relation?.Name}
                </strong>
                <div className="d-flex align-items-center gap-1">
                  <div className="task-day-badge">
                    {taskDay}
                  </div>
                  {task.ActualEndTime
                    ? renderStatusBadge(task.Status, t)
                    : task.ActualStartTime
                      ? <span className="badge bg-info text-white">{t("inProgressLable")}</span>
                      : null
                  }
                </div>
              </div>
              <div className={`d-flex align-items-start mb-1 data-font-colour`}>
                <FontAwesomeIcon icon={faTasks} className="me-2 mt-1 icon-opacity" />
                <span className="data-font-colour data-font-size">{task.Task?.TaskName}</span>
              </div>
              <div className="mb-1 data-font-colour">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2 icon-opacity" />
                <a className="data-font-size"
                  href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(task.Relation?.StreetName + ' ' + task.Relation?.HouseNumber + ', ' + task.Relation?.City)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {`${task.Relation?.StreetName || ''} ${task.Relation?.HouseNumber || ''}, ${task.Relation?.PostCode || ''} ${task.Relation?.City || ''}`}
                </a>
              </div>

              <div className={`d-flex align-items-start mb-2 data-font-colour`}>
                <FontAwesomeIcon icon={faClock} className="me-2 mt-1 icon-opacity data-font-colour" />
                <div className="data-font-size">
                  {formatTaskDateTimeRange(task.WeekStartDate, task.StartTime, task.EndTime)}
                </div>
              </div>
            </div>

            <div className="d-flex justify-content-between align-items-end mt-2">
              <div className="flex-grow-1 p-2" style={{ maxWidth: "260px" }}>
                <h6 className="fw-semibold mb-2 small">{t("taskProgress")}</h6>

                {/* Completed */}
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span className="text-muted small">{t("completedLable")}</span>
                  <span className="badge text-success fw-semibold py-0 px-2" style={{ backgroundColor: "#d1f4e0", fontSize: "0.75rem" }}>
                    {completed}
                  </span>
                </div>
                <div className="progress mb-2" style={{ height: "4px" }}>
                  <div
                    className="progress-bar bg-success"
                    role="progressbar"
                    style={{ width: `${total > 0 ? (completed / total) * 100 : 0}%` }}
                  ></div>
                </div>

                {/* Pending */}
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span className="text-muted small">{t("pending")}</span>
                  <span className="badge text-danger fw-semibold py-0 px-2" style={{ backgroundColor: "#fde3d1", fontSize: "0.75rem" }}>
                    {pending}
                  </span>
                </div>
                <div className="progress" style={{ height: "4px" }}>
                  <div
                    className="progress-bar bg-warning"
                    role="progressbar"
                    style={{ width: `${total > 0 ? (pending / total) * 100 : 0}%` }}
                  ></div>
                </div>
              </div>
              <div className="align-self-end">
                {!task.ActualEndTime && (Number(task.Status) !== 1 ? (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="fw-semibold px-2 py-1 rounded-pill shadow-sm"
                    title={t("startCurrentJob")}
                    onClick={(e) => handleStartJobClick(e, task)}
                  >
                    {t("startJob")}
                  </Button>
                ) : (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="fw-semibold px-2 py-1 rounded-pill shadow-sm"
                    title={t("endCurrentJob")}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedTask(task);
                      setDrawerOpen(true);
                    }}
                  >
                    {t("endJob")}
                  </Button>
                )
                )}
              </div>
            </div>
          </Card.Body>
        </Card>
      </Col>
    );
  };

  const renderStatusBadge = (status, t) => {
    const statusNumberToKey = {
      0: "planned",
      1: "inprogress",
      2: "jobcomplete",
      3: "unabletoaccesslocation",
      4: "customernotavailable",
      5: "equipmentissue",
      6: "cancelledbycustomer",
      7: "incomplete"
    };

    let normalizedStatus = "";

    if (!isNaN(status) && status !== null && status !== "") {
      normalizedStatus = statusNumberToKey[Number(status)] || "";
    } else {
      normalizedStatus = (status || "").toString().replace(/\s+/g, "").toLowerCase();
    }

    const variantMap = {
      planned: "primary",
      inprogress: "info",
      jobcomplete: "success",
      unabletoaccesslocation: "warning",
      customernotavailable: "warning",
      equipmentissue: "warning",
      cancelledbycustomer: "danger",
      incomplete: "warning"
    };

    const variant = variantMap[normalizedStatus] || "info";

    const translatedText = t?.(`orderDetailStatus.${normalizedStatus}`) || status;

    return (
      <Badge pill bg={variant} className="me-2">
        {translatedText}
      </Badge>
    );
  };

  useEffect(() => {
    fetchEmployeeTasks();
    loadAttendance();
  }, []);

  useSocket('orderDetail:updated', () => {
    fetchEmployeeTasks(true);
  });

  useSocket('orderDetail:deleted', () => {
    fetchEmployeeTasks(true);
  });

  useSocket('orderDetail:created', (data) => {
    const userId = localStorage.getItem("UserId");
    if (Array.isArray(data?.cleanerIds) && !data.cleanerIds.includes(userId)) return;
    fetchEmployeeTasks(true);
  });

  useSocket('order:created', (data) => {
    const userId = localStorage.getItem("UserId");
    if (Array.isArray(data?.cleanerIds) && !data.cleanerIds.includes(userId)) return;
    fetchEmployeeTasks(true);
  });

  const TaskCard = ({ task, onClick, t, renderStatusBadge, stepCompletionsForTask = {} }) => {
    const { bg } = getStatusColorClassesForTile(task.Status);
    const { total, completed, pending } = getTaskProgress(task, stepCompletionsForTask);

    return (
      <Col xs={12} md={6} lg={4}>
        <Card
          className={`cursor-pointer h-100 border px-3 py-2 card-hover ${bg}`}
          onClick={() => onClick(task)}
        >
          <Card.Body className={`p-2 data-font-colour d-flex flex-column justify-content-between`}>
            <div>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <strong className="fs-9 text-truncate">
                  {task.Relation?.Name}
                </strong>

                <div className="text-end">
                  {task.StartTime && task.EndTime ? (() => {
                    const now = moment();

                    const todayStr = moment().format("YYYY-MM-DD");
                    const start = moment(`${todayStr}T${moment(task.StartTime).format("HH:mm:ss")}`);
                    const end = moment(`${todayStr}T${moment(task.EndTime).format("HH:mm:ss")}`);

                    const actualStart = task.ActualStartTime ? moment(task.ActualStartTime).local() : null;
                    const actualEnd = task.ActualEndTime ? moment(task.ActualEndTime).local() : null;

                    const taskDateStr = moment(task.WeekStartDate).format("YYYY-MM-DD");
                    const actualStartDate = task.ActualStartDate
                      ? moment(task.ActualStartDate).format("YYYY-MM-DD")
                      : null;

                    const startedLate = (() => {
                      if (!actualStart || !start.isValid() || !actualStartDate) return false;
                      if (actualStartDate < taskDateStr) return false;
                      if (actualStartDate > taskDateStr) return true;

                      const scheduledMinutes = start.format("HH:mm");
                      const actualMinutes = actualStart.format("HH:mm");
                      return actualMinutes > scheduledMinutes;
                    })();

                    const renderBadge = (label, bg) => (
                      <div className="d-inline-flex align-items-center gap-2">
                        <span className={`badge bg-${bg} text-white`}>{label}</span>
                        {startedLate && (
                          <FontAwesomeIcon
                            icon={faExclamationCircle}
                            className="text-warning cursor-pointer"
                            style={{ fontSize: "1.2rem" }}
                            title={t("jobStartedLate")}
                            onClick={(e) => {
                              e.stopPropagation();
                              toast.warning(t("jobStartedLate"), { position: "top-right" });
                            }}
                          />
                        )}
                      </div>
                    );

                    if (actualEnd) {
                      return (
                        <div className="d-inline-flex align-items-center gap-2">
                          {renderStatusBadge(task.Status, t)}
                          {startedLate && (
                            <FontAwesomeIcon
                              icon={faExclamationCircle}
                              className="text-warning cursor-pointer"
                              style={{ fontSize: "1.2rem" }}
                              title={t("jobStartedLate")}
                              onClick={(e) => {
                                e.stopPropagation();
                                toast.warning(t("jobStartedLate"), {
                                  position: "top-right"
                                });
                              }}
                            />
                          )}
                        </div>
                      );
                    }

                    if (Number(task.Status) === 1) return renderBadge(t("inProgressLable"), "info");

                    if (now.isBefore(start)) {
                      const duration = moment.duration(start.diff(now));
                      const hours = duration.hours();
                      const minutes = duration.minutes();
                      return (
                        <span className="badge bg-primary text-white">
                          {t("startingIn")}{" "}
                          {hours > 0 ? `${hours}${t("hourUnit")} ` : ""}
                          {minutes > 0 ? `${minutes}${t("minutes")}` : ""}
                        </span>
                      );
                    }

                    if (now.isBetween(start, end)) {
                      return renderBadge(t("pendingStart"), "warning");
                    }

                    return renderBadge(t("jobNotCompleted"), "danger");
                  })() : (
                    <span className="badge bg-primary text-white">{t("scheduled")}</span>
                  )}
                </div>
              </div>

              <div className={`d-flex align-items-start mb-1 data-font-colour`}>
                <FontAwesomeIcon icon={faTasks} className="me-2 mt-1 icon-opacity" />
                <span className="data-font-size">{task.Task?.TaskName}</span>
              </div>

              <div className="mb-1">
                <FontAwesomeIcon icon={faMapMarkerAlt} className="me-2 icon-opacity" />
                <a className="data-font-size" href={`https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(task.Relation?.StreetName + ' ' + task.Relation?.HouseNumber + ', ' + task.Relation?.City)}`} target="_blank" rel="noopener noreferrer">
                  {`${task.Relation?.StreetName || ''} ${task.Relation?.HouseNumber || ''}, ${task.Relation?.PostCode || ''} ${task.Relation?.City || ''}`}
                </a>
              </div>

              <div className={`d-flex align-items-start mb-2 data-font-colour`}>
                <FontAwesomeIcon icon={faClock} className="me-2 mt-1 icon-opacity" />
                <div className="data-font-size">
                  {formatTaskDateTimeRange(task.WeekStartDate, task.StartTime, task.EndTime)}
                </div>
              </div>

            </div>
            <div className="d-flex justify-content-between align-items-end mt-2">
              <div className="flex-grow-1 p-2" style={{ maxWidth: "260px" }}>
                <h6 className="fw-semibold mb-2 small">{t("taskProgress")}</h6>

                {/* Completed */}
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span className="text-muted small">{t("completedLable")}</span>
                  <span className="badge text-success fw-semibold py-0 px-2" style={{ backgroundColor: "#d1f4e0", fontSize: "0.75rem" }}>
                    {completed}
                  </span>
                </div>
                <div className="progress mb-2" style={{ height: "4px" }}>
                  <div
                    className="progress-bar bg-success"
                    role="progressbar"
                    style={{ width: `${total > 0 ? (completed / total) * 100 : 0}%` }}
                  ></div>
                </div>

                {/* Pending */}
                <div className="d-flex align-items-center justify-content-between mb-1">
                  <span className="text-muted small">{t("pending")}</span>
                  <span className="badge text-danger fw-semibold py-0 px-2" style={{ backgroundColor: "#fde3d1", fontSize: "0.75rem" }}>
                    {pending}
                  </span>
                </div>
                <div className="progress" style={{ height: "4px" }}>
                  <div
                    className="progress-bar bg-warning"
                    role="progressbar"
                    style={{ width: `${total > 0 ? (pending / total) * 100 : 0}%` }}
                  ></div>
                </div>
              </div>

              <div className="align-self-end">
                {!task.ActualEndTime && (Number(task.Status) !== 1 ? (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="fw-semibold px-2 py-1 rounded-pill shadow-sm"
                    title={t("startCurrentJob")}
                    onClick={(e) => handleStartJobClick(e, task)}
                  >
                    {t("startJob")}
                  </Button>
                ) : (
                  <Button
                    variant="outline-danger"
                    size="sm"
                    className="fw-semibold px-2 py-1 rounded-pill shadow-sm"
                    title={t("endCurrentJob")}
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedTask(task);
                      setDrawerOpen(true);
                    }}
                  >
                    {t("endJob")}
                  </Button>
                )
                )}
              </div>
            </div>
          </Card.Body>
        </Card>
      </Col>
    );
  };

  useEffect(() => {
    const isEmployeeView = sessionStorage.getItem("isEmployeeView");

    if (isEmployeeView === "true") {
      // Admin is viewing as employee — bypass claim check
      setHasAccess(true);
      setIsLoading(false);
      return;
    }

    fetchRoleAndClaims({
      navigate,
      claimType: "employeeDashboard",
      claimValue: "view",
      setHasAccess,
      setIsLoading,
    });
  }, [navigate]);

  const loadingOrRedirect = handleLoadingAndAccess(
    isLoading,
    hasAccess,
  );

  if (loadingOrRedirect) {
    return loadingOrRedirect;
  }

    const handleRingAction = () => {
    if (attendancePhase === "idle" && !attendanceBusy) {
      handleClockIn();
    } else if (attendancePhase === "done") {
      navigate("/my-timesheet");
    }
  };

  const ringLabel =
    attendancePhase === "idle"
      ? t("clockIn", "Clock-in")
      : attendancePhase === "done"
      ? t("viewTimesheet", "View Timesheet")
      : undefined;
  
  const DEFAULT_WORK_START = process.env.REACT_APP_DEFAULT_WORK_START || "09:00:00";
  const DEFAULT_WORK_END = process.env.REACT_APP_DEFAULT_WORK_END || "17:00:00";

  const shiftStart = moment(DEFAULT_WORK_START, "HH:mm:ss").format("HH:mm");
  const shiftEnd = moment(DEFAULT_WORK_END, "HH:mm:ss").format("HH:mm");
  const shiftHours = Math.round(
    moment(DEFAULT_WORK_END, "HH:mm:ss").diff(moment(DEFAULT_WORK_START, "HH:mm:ss"), "hours", true)
  ) || 8;    

  return (
    <>
      <Row className="g-3 mb-3">
        <Col lg={12} xxl={12}>
          <Card className="mb-3 h-100">
            <Background
              image={corner4}
              className="bg-card"
              style={{
                borderTopRightRadius: "0.375rem",
                borderBottomRightRadius: "0.375rem",
              }}
            />
            <Card.Body className="position-relative pe-4 pb-2">
              <Row className="flex-between-center">
                <Col md>
                  <h5 className="mb-2 mb-md-0">{t("dashboard")}</h5>
                </Col>
                {isEmployeeView === "true" && (
                  <Col md="auto">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={() => {
                        sessionStorage.setItem("isEmployeeView", "false");
                        setIsEmployeeView(false);
                        navigate("/dashboard");
                      }}
                    >
                      {t("adminDashboard")}
                    </Button>
                  </Col>
                )}
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center my-5">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">{t("loading")}</span>
          </Spinner>
        </div>
      ) : (
        <>
          {isAttendanceEnabled && (attendancePhase === "idle" || attendancePhase === "done") && (
            <Card className="attendance-card-enter position-relative overflow-hidden mb-3 border rounded-3 shadow-none bg-white">
              <div
                className={`position-absolute top-0 start-0 bottom-0 border-start border-4 ${
                  attendancePhase === "done" ? "border-success" : "border-primary"
                }`}
              />
              <Card.Body className="px-md-4 py-md-2">
                <div className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                  <div className="py-1">
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <span className="data-font-colour data-font-size">
                         {t("attendance")} &middot; {moment().format("ddd, DD-MM-YYYY")}
                      </span>
                      {attendancePhase === "idle" && (
                        <SubtleBadge pill bg="secondary">
                          {t("notClockedIn")}
                        </SubtleBadge>
                      )}
                      {attendancePhase === "done" && (
                        <SubtleBadge pill bg="success">
                          {t("checkOut", "Check-out")}
                        </SubtleBadge>
                      )}
                    </div>

                    <h5 className="mb-2 text-capitalize">
                      {attendancePhase === "done"
                        ? t("shiftCompleted")
                        : `${moment().hour() < 12 ? t("goodMorning") : moment().hour() < 17 ? t("goodAfternoon") : t("goodEvening")}, ${(localStorage.getItem("UserName") || "").toLowerCase()}`}
                    </h5>

                    {(() => {
                      const now = moment();
                      const isStartingSoon = now.isBetween(
                        moment().set({ hour: 8, minute: 45, second: 0 }),
                        moment().set({ hour: 9, minute: 0, second: 0 })
                      );

                      return (
                        <div className="d-flex align-items-center gap-2 mb-0">
                          {attendancePhase === "idle" ? (
                            <>
                              <span className="data-font-colour data-font-size">
                               {shiftStart} &ndash; {shiftEnd} &middot; {shiftHours}{t("hourShort", "h")}
                              </span>
                              {isStartingSoon && (
                                <SubtleBadge pill bg="primary">
                                  {t("startsIn15m")}
                                </SubtleBadge>
                              )}
                            </>
                          ) : (
                            <span className="data-font-colour data-font-size">
                              {shiftStart} &ndash; {shiftEnd} &middot; {t("worked")} {formatSecondsToHM(workSeconds)}
                            </span>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                    <div className="d-flex flex-column align-items-center">                   
                    <div
                      id="dashboard-clockin-ring"
                      role="button"
                      tabIndex={0}
                      className="position-relative d-inline-flex align-items-center justify-content-center cursor-pointer flex-shrink-0"
                      onClick={handleRingAction}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleRingAction();
                        }
                      }}
                      title={ringLabel}
                      aria-label={ringLabel}
                    >
                      <svg width="160" height="160" viewBox="0 0 160 160">
                        {attendancePhase === "idle" && (
                          <circle
                            cx="80"
                            cy="80"
                            r="63"
                            fill="none"
                            stroke="rgba(44, 123, 229, 0.45)"
                            strokeWidth="2.5"
                          >
                            <animate
                              attributeName="r"
                              values="63; 78"
                              dur="2.2s"
                              repeatCount="indefinite"
                            />
                            <animate
                              attributeName="opacity"
                              values="0.9; 0"
                              dur="2.2s"
                              repeatCount="indefinite"
                            />
                          </circle>
                        )}

                        <circle
                          cx="80"
                          cy="80"
                          r="63"
                          fill="#f8fafd"
                          stroke={attendancePhase === "done" ? "#16a34a" : "#dce7f8"}
                          strokeWidth="5.5"
                        />
                        {attendancePhase === "idle" && (
                          <circle cx="80" cy="17" r="3" fill="#2c7be5" />
                        )}
                      </svg>

                        <div className="position-absolute top-50 start-50 translate-middle text-center w-100">
                        {attendancePhase === "idle" ? (
                          <>
                            <FontAwesomeIcon icon={faClock} className="text-primary fs-7 mb-2 d-block mx-auto" />
                            <span className="text-primary fs-10 fw-semibold d-block lh-1 mt-1">{t("clockIn")}</span>
                          </>
                        ) : (
                          <>
                            <FontAwesomeIcon icon={faCheckCircle} className="text-success fs-6 mb-2 d-block mx-auto" />
                            <div className="fs-10 fw-semibold lh-1 mb-1 mt-1">
                              {formatSecondsToHM(workSeconds)}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          )}

          {/* Today's and Overdue Tasks Card with Tabs */}
          <Card className="mb-3">
            <Card.Header className="d-flex justify-content-between align-items-center border-bottom">
              <h5 className="mb-0">{t("myTasks")}</h5>
              {((activeTab === "today" && (todayTasks.length > 3 || upcomingTasks.length > 3)) ||
                (activeTab === "overdue" && overdueTasks.length > 4)) && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      if (activeTab === "today") {
                        setShowAllTodayTasks(!showAllTodayTasks);
                        setShowAllUpcomingTasks(!showAllUpcomingTasks);
                      } else {
                        setShowAllOverdueTasks(!showAllOverdueTasks);
                      }
                    }}
                  >
                    {activeTab === "today"
                      ? (showAllTodayTasks ? t("showLess") : t("showAll"))
                      : (showAllOverdueTasks ? t("showLess") : t("showAll"))
                    }
                  </Button>
                )}
            </Card.Header>
            <Card.Body className="py-3 px-3">
              <Tab.Container activeKey={activeTab} onSelect={setActiveTab}>
                <Nav variant="tabs" className="border-bottom-0">
                  <Nav.Item>
                    <Nav.Link
                      eventKey="today"
                      className="ps-0 cursor-pointer outline-none"
                    >
                      {t("todaysTask")}
                    </Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link
                      eventKey="overdue"
                      className="px-2 px-md-3 cursor-pointer outline-none"
                    >
                      {t("overdueTasks")}
                      {overdueTasks.length > 0 && (
                        <Badge bg="danger" className="ms-2">
                          {overdueTasks.length}
                        </Badge>
                      )}
                    </Nav.Link>
                  </Nav.Item>
                </Nav>
                <Tab.Content>
                  {/* Today's Tasks Tab */}
                  <Tab.Pane eventKey="today" className="mt-3">
                    <Row className="g-3">
                      {todayTasks.length === 0 ? (
                        <Col>
                          <p className="text-muted small mb-0">{t("noTasksToday")}</p>
                        </Col>
                      ) : (
                        todayTasks
                          .slice()
                          .sort(compareTasksAsc)
                          .slice(0, showAllTodayTasks ? todayTasks.length : 3)
                          .map((task, index) => (
                            <TaskCard
                              key={index}
                              task={task}
                              onClick={(t) => {
                                setSelectedTask(t);
                                setDrawerOpen(true);
                              }}
                              t={t}
                              renderStatusBadge={renderStatusBadge}
                              stepCompletionsForTask={taskStepCompletions[task.OrderDetailId] || {}}
                            />
                          ))
                      )}
                    </Row>

                    {/* Separation Line */}
                    <hr className="my-4" />

                    <div className="mb-3">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h5 className="nav-link upcoming-task-title">
                          {t("upcomingTasks")}
                        </h5>
                      </div>
                      <Row className="g-3">
                        {upcomingTasks.length === 0 ? (
                          <Col>
                            <p className="text-muted small mb-0">{t("noUpcomingTasks")}</p>
                          </Col>
                        ) : (
                          upcomingTasks
                            .slice()
                            .sort(compareTasksAsc)
                            .slice(0, showAllUpcomingTasks ? upcomingTasks.length : 3)
                            .map((task, index) => (
                              <UpcomingTaskCard
                                key={`upcoming-${index}`}
                                task={task}
                                onClick={(t) => {
                                  setSelectedTask(t);
                                  setDrawerOpen(true);
                                }}
                                t={t}
                                renderStatusBadge={renderStatusBadge}
                                stepCompletionsForTask={taskStepCompletions[task.OrderDetailId] || {}}
                              />
                            ))
                        )}
                      </Row>
                    </div>
                  </Tab.Pane>
                  {/* Overdue Tasks Tab */}
                  <Tab.Pane eventKey="overdue" className="mt-3">
                    <Row className="g-3">
                      {overdueTasks.length === 0 ? (
                        <Col>
                          <p className="text-muted small mb-0">{t("noOverdueTasks")}</p>
                        </Col>
                      ) : (
                        overdueTasks
                          .slice()
                          .sort(compareTasksDesc)
                          .slice(0, showAllOverdueTasks ? overdueTasks.length : 4)
                          .map((task, index) => (
                            <OverdueTaskCard
                              key={`overdue-${index}`}
                              task={task}
                              onClick={(t) => {
                                setSelectedTask(t);
                                setDrawerOpen(true);
                              }}
                              t={t}
                              renderStatusBadge={renderStatusBadge}
                            />
                          ))
                      )}
                    </Row>
                  </Tab.Pane>
                </Tab.Content>
              </Tab.Container>
            </Card.Body>
          </Card>

          {/* Completed Tasks Table */}
          <AdvanceTableProvider {...completedTasksTable}>
            <Card>
              <Card.Header>
                <Row className="align-items-center justify-content-between">
                  <Col xs={12} sm={6} md={6}>
                    <h5 className="mb-0">{t("completedTasks")}</h5>
                  </Col>
                  <Col
                    xs={12}
                    sm={6}
                    md={6}
                    className="d-flex justify-content-end"
                  >
                    <div style={{ minWidth: "200px" }}>
                      <AdvanceTableSearchBox setGlobalFilter={setCompletedSearchInput} />
                    </div>
                  </Col>
                </Row>
              </Card.Header>
              <Card.Body className="p-0">
                {completedLoading ? (
                  <div className="text-center py-5">
                    <Spinner animation="border" size="sm" role="status">
                      <span className="visually-hidden">{t("loading")}</span>
                    </Spinner>
                  </div>
                ) : completedTasks.length === 0 ? (
                  <div className="text-center py-5">
                    <p className="text-muted">{t("noCompletedTasks")}</p>
                  </div>
                ) : (
                  <AdvanceTable
                    headerClassName="bg-200 text-nowrap align-middle"
                    rowClassName="align-middle white-space-nowrap cursor-pointer"
                    tableProps={{
                      bordered: true,
                      striped: true,
                      size: "sm",
                      className: "mb-0 overflow-hidden data-font-size table-hover",
                    }}
                    getRowProps={(row) => ({
                      onClick: () => {
                        setSelectedTask(row.original);
                        setDrawerOpen(true);
                      },
                      style: { cursor: "pointer" },
                    })}
                  />
                )}
              </Card.Body>
              <Card.Footer>
                <ManualPaginationFooter
                  totalCount={completedTotalCount}
                  pageIndex={completedPageIndex}
                  pageSize={completedPageSize}
                  tableKey="completedTasksTable"
                  onPageSizeChange={(newSize) => {
                    setCompletedPageSize(newSize);
                    setCompletedPageIndex(0);
                  }}
                />
              </Card.Footer>
            </Card>
          </AdvanceTableProvider>
        </>
      )}

      <TaskDrawer
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
        }}
        onStepCompletionsChange={(orderDetailId, newMap) => {
          setTaskStepCompletions(prev => ({
            ...prev,
            [orderDetailId]: newMap
          }));
        }}
        selectedTask={selectedTask}
        t={t}
        renderStatusBadge={renderTaskStatusBadge}
        showStartButton={true}
        userRole={userRole}
        fetchEmployeeTasks={fetchEmployeeTasks}
        onStartJob={handleStartJobClick}
      />
    </>
  );
};

export default EmployeeDashboard;